<script lang="ts">
  import { get } from 'svelte/store'
  import { aiFeaturesStore } from '$lib/stores/aiFeatures'
  import { diffRenderer } from '$lib/services/DiffRenderer'
  import type { Segment } from '$lib/stores/aiFeatures'

  /**
   * SegmentEditorController: Handles segment editing functionality
   * Single Responsibility: Manage inline editing of segments
   */

  interface Props {
    segments: Segment[]
    isProcessing: boolean
    editingSegmentId: string | null
    onSegmentsChange?: (segments: Segment[]) => void
  }

  let { 
    segments = $bindable([]),
    isProcessing,
    editingSegmentId = $bindable(null),
    onSegmentsChange
  }: Props = $props()

  // Custom action to focus element on mount
  export function focusOnMount(node: HTMLElement) {
    // Only set focus once when initially mounting
    if (!node.dataset.focused) {
      node.dataset.focused = 'true'
      setTimeout(() => {
        node.focus()
        // Place cursor at the end
        const selection = window.getSelection()
        const range = document.createRange()
        range.selectNodeContents(node)
        range.collapse(false)
        selection?.removeAllRanges()
        selection?.addRange(range)
      }, 10)
    }
  }

  // Handle segment editing
  export function startEditing(segmentId: string, currentText: string) {
    // Don't allow editing if processing
    if (isProcessing) return
    
    const segment = segments.find(s => s.id === segmentId)
    if (segment?.isProcessing) return
    
    editingSegmentId = segmentId
    
    // Disable scrolling on the panel while editing
    const panel = document.querySelector('.panel.suggested .panel-content') as HTMLElement
    if (panel) {
      panel.style.overflow = 'hidden'
    }
  }

  export function cancelEditing() {
    editingSegmentId = null
    
    // Re-enable scrolling on the panel
    const panel = document.querySelector('.panel.suggested .panel-content') as HTMLElement
    if (panel) {
      panel.style.overflow = ''
    }
  }

  export function saveEditing(segmentId: string, newText: string) {
    const segmentIndex = segments.findIndex(s => s.id === segmentId)
    if (segmentIndex === -1) {
      cancelEditing()
      return
    }
    
    const segment = segments[segmentIndex]
    const currentText = segment.suggested || segment.original
    
    // If no changes, just cancel (but allow empty text for deletion)
    if (newText === currentText) {
      cancelEditing()
      return
    }
    
    // If text is completely deleted, handle as special case
    if (!newText || newText.trim() === '') {
      newText = '' // Ensure it's an empty string, not null/undefined
    }
    
    // Generate compact diff for manual edit
    let reasonText = '사용자가 직접 수정했습니다'
    
    // Check if entire content was deleted
    if (newText === '' && currentText) {
      reasonText = 'manual-edit-diff:' + JSON.stringify({
        removed: [currentText.length > 100 ? currentText.substring(0, 100) + '...' : currentText],
        added: []
      })
    } else {
      const compactDiff = diffRenderer.getCompactDiff(currentText, newText)
      // Add compact diff info to reason
      if (compactDiff.removed.length > 0 || compactDiff.added.length > 0) {
        reasonText = 'manual-edit-diff:' + JSON.stringify(compactDiff)
      }
    }
    
    // Update the segment suggestion with manual edit
    aiFeaturesStore.updateSegmentSuggestion(
      segmentId,
      newText,
      reasonText,
      'manual-edit'
    )
    
    // Save to history as manual edit
    aiFeaturesStore.saveSegmentHistory(segmentId, 'manual-edit')
    
    // Update local segments from store
    const storeState = get(aiFeaturesStore)
    segments = storeState.segments
    
    // Clear editing state
    editingSegmentId = null
    
    // Re-enable scrolling on the panel
    const panel = document.querySelector('.panel.suggested .panel-content') as HTMLElement
    if (panel) {
      panel.style.overflow = ''
    }
    
    // Call the change callback
    onSegmentsChange?.(segments)
  }

  // Handle keyboard events for editing
  export function handleKeydown(e: KeyboardEvent, segmentId: string) {
    if (e.key === 'Escape') {
      e.preventDefault()
      cancelEditing()
    } else if (e.key === 'Enter') {
      // Insert line break manually to prevent cursor jump
      e.preventDefault()
      document.execCommand('insertLineBreak')
    }
  }

  // Handle click outside to save edits
  $effect(() => {
    if (!editingSegmentId) return
    
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as HTMLElement
      const editableElement = document.querySelector(`[data-editing-id="${editingSegmentId}"]`)
      
      // Don't handle if clicking on the editable element itself
      if (editableElement && !editableElement.contains(target)) {
        // Delay to allow blur event to fire first
        setTimeout(() => {
          if (editingSegmentId) {
            const newText = (editableElement as HTMLElement).innerText.trim()
            saveEditing(editingSegmentId, newText)
          }
        }, 0)
      }
    }
    
    // Use capture phase to ensure we get the event before blur
    document.addEventListener('click', handleClickOutside, true)
    
    return () => {
      document.removeEventListener('click', handleClickOutside, true)
    }
  })
</script>